import { Flex, Grid } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { SectionHeader, Card, Text, Icon, Heading } from "@ttc3k/trekker";
import { Calendar, KeyCommand, List, SystemRestart } from "iconoir-react";
import { useLoggedInAs } from "shared/hooks";
import { type AssetImage, useOrganizationLogoByIdQuery } from "gql/generated";
import { useNavigate } from "react-router-dom";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import {
  ClickableCardSurface,
  CloudinaryImage,
  LoadingPage,
} from "shared/components";
import { css } from "styled-system/css";
import { isLandscape } from "shared/utils";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";

interface AppCardProps {
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
  route: string;
}

export const Home = () => {
  const loggedInAs = useLoggedInAs();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, error, loading } = useOrganizationLogoByIdQuery({
    variables: { id: loggedInAs?.entityId },
  });

  if (loading) return <LoadingPage />;

  if (error) {
    throw new Error(error.message);
  }

  if (!data || !data.organizationById) {
    throw new Error("Cannot find organization");
  }

  const appsConfig: AppCardProps[] = [
    {
      Icon: KeyCommand,
      title: t("home:APPS.DIRECTORY.TITLE"),
      description: t("home:APPS.DIRECTORY.DESCRIPTION"),
      route: "/directory",
    },
    {
      Icon: List,
      title: t("home:APPS.CUSTOM_LISTS.TITLE"),
      description: t("home:APPS.CUSTOM_LISTS.DESCRIPTION"),
      route: "/custom-lists",
    } /*
    {
      Icon: StatsReport,
      title: t("home:APPS.CAMPAIGNS.TITLE"),
      description: t("home:APPS.CAMPAIGNS.DESCRIPTION"),
      route: "/campaigns",
    }, */,
    {
      Icon: SystemRestart,
      title: t("home:APPS.WIDGETS.TITLE"),
      description: t("home:APPS.WIDGETS.DESCRIPTION"),
      route: "/widgets",
    },
    {
      Icon: Calendar,
      title: t("home:APPS.EVENTS.TITLE"),
      description: t("home:APPS.EVENTS.DESCRIPTION"),
      route: "/events",
    },
  ];

  const handleSelectApp = (app: AppCardProps) => {
    navigate(".." + app.route);
  };

  return (
    <Flex
      flexDir="column"
      alignItems={"center"}
      px={"800"}
      py={"1000"}
      w={"full"}
      h={"full"}
      bg={"bg.wa.light"}
      overflowY="auto"
    >
      <Flex
        direction={"column"}
        gap={"600"}
        alignItems={"center"}
        justifyContent={"center"}
        maxW="800px"
      >
        {data?.organizationById?.logo && (
          <CloudinaryImage
            image={data.organizationById.logo as AssetImage}
            imageStyleProps={{
              objectFit: "scale-down",
              className: css({
                h: "130px",
                w: "260px",
              }),
            }}
            resize={
              isLandscape(data.organizationById.logo)
                ? limitFill().width(260).gravity(autoGravity())
                : limitFill().height(130).gravity(autoGravity())
            }
          />
        )}
        <Flex direction={"column"} justifyContent="center" gap={"250"}>
          <SectionHeader text={t("home:HEADER.TITLE")} divider />
          <Grid gap={"200"} columns={2}>
            {appsConfig.map((app) => (
              <ClickableCardSurface
                key={app.title}
                onClick={() => handleSelectApp(app)}
              >
                <Card.Body>
                  <Card.BodyContent>
                    <Flex direction={"column"} gap={"100"}>
                      <Icon Element={app.Icon} size={"lg"} />
                      <Heading size={"h5"}>{app.title}</Heading>
                    </Flex>
                  </Card.BodyContent>
                  <Card.BodyContent>
                    <Text color={"text.mid"} visual={"smallMedium"}>
                      {app.description}
                    </Text>
                  </Card.BodyContent>
                </Card.Body>
              </ClickableCardSurface>
            ))}
          </Grid>
        </Flex>
      </Flex>
    </Flex>
  );
};
